"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom"
import { useLogin, usePasswordReset } from "@/hooks/useApi"
import { useAuth } from "@/providers/auth-provider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { MessageSquare } from "lucide-react"
import { toast } from "sonner"
import { prefetchAllRoutes } from "@/utils/route-prefetch"

export function LoginPage() {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const redirect = searchParams.get("redirect") || "/dashboard"
    const isForgotPassword = searchParams.get("mode") === "forgot-password"
    
    // Check if we were redirected from password reset with a success message
    const successMessage = location.state?.message

    const { login, loading: isLoginPending } = useLogin()
    const { requestReset, loading: isResetPending } = usePasswordReset()
    const { refreshUser } = useAuth()

    // Update URL when mode changes
    const setMode = (mode: "login" | "forgot-password") => {
        setSearchParams(prev => {
            if (mode === "login") {
                prev.delete("mode")
            } else {
                prev.set("mode", mode)
            }
            return prev
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (isForgotPassword) {
            try {
                const response = await requestReset(email)
                if (response.code === "success") {
                    toast.success(response.message)
                    setMode("login")
                } else {
                    toast.error(response.message)
                }
            } catch (error) {
                // Error handling is done automatically in the requestReset hook
                console.error('Password reset failed:', error)
            }
            return
        }

        try {
            await login(email, password)
            // Refresh the user data to update auth state
            await refreshUser()
            // Prefetch route chunks after successful login
            prefetchAllRoutes()
            // Login successful, navigate to redirect
            navigate(redirect)
        } catch (error) {
            // Error handling is done automatically in the login hook
            console.error('Login failed:', error)
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1 text-center">
                    <div className="flex justify-center mb-2">
                        <div className="rounded-full bg-primary/10 p-2">
                            <MessageSquare className="h-6 w-6 text-primary" />
                        </div>
                    </div>
                    <CardTitle className="text-2xl">MessageLink</CardTitle>
                    <CardDescription>
                        {isForgotPassword
                            ? "Enter your email to reset your password"
                            : "Enter your credentials to sign in to your account"}
                    </CardDescription>
                    {successMessage && (
                        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
                            {successMessage}
                        </div>
                    )}
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="name@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        {!isForgotPassword && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <button
                                        type="button"
                                        onClick={() => setMode("forgot-password")}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        )}
                    </CardContent>
                    <CardFooter className="flex flex-col">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={isLoginPending || isResetPending}
                        >
                            {isForgotPassword
                                ? (isResetPending ? "Sending..." : "Send Reset Link")
                                : (isLoginPending ? "Signing in..." : "Sign In")}
                        </Button>
                        {isForgotPassword ? (
                            <p className="mt-4 text-center text-sm text-muted-foreground">
                                Remember your password?{" "}
                                <button
                                    type="button"
                                    onClick={() => setMode("login")}
                                    className="text-primary hover:underline"
                                >
                                    Sign in
                                </button>
                            </p>
                        ) : (
                            <p className="mt-4 text-center text-sm text-muted-foreground hidden">
                                Don't have an account?{" "}
                                <Link to="/register" className="text-primary hover:underline">
                                    Sign up
                                </Link>
                            </p>
                        )}
                    </CardFooter>
                </form>
            </Card>
        </div>
    )
} 